import { useMemo, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { PageHeader, PrimaryButton, Modal, Field, TextInput, Select, useFetch } from "../../components/crm/ui";
import { Plus, Trash2, Edit3, MapPin, Check } from "lucide-react";
import { INDIA_STATES_DISTRICTS } from "../../data/indiaStatesDistricts";

export default function PermissibleTerritories() {
  const { data, reload } = useFetch("/permissible-territories");

  // New Territory: State dropdown + multi-select districts (auto-populated per state)
  const [open, setOpen] = useState(false);
  const [newState, setNewState] = useState("");
  const [selectedDistricts, setSelectedDistricts] = useState([]);
  const [saving, setSaving] = useState(false);

  // Edit: only excluded PINs are editable; state/district are fixed identity of the row
  const [editing, setEditing] = useState(null);
  const [editPins, setEditPins] = useState("");
  const [editSaving, setEditSaving] = useState(false);

  const alreadyListed = useMemo(
    () => new Set((data || []).filter((t) => t.state === newState).map((t) => t.district)),
    [data, newState]
  );
  const districtOptions = useMemo(() => {
    const entry = INDIA_STATES_DISTRICTS.find((s) => s.state === newState);
    return entry ? entry.districts : [];
  }, [newState]);

  function startCreate() { setNewState(""); setSelectedDistricts([]); setOpen(true); }
  function toggleDistrict(d) {
    setSelectedDistricts((prev) => (prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d]));
  }

  async function save(e) {
    e.preventDefault();
    if (!newState) { toast.error("Select a state"); return; }
    if (selectedDistricts.length === 0) { toast.error("Select at least one district"); return; }
    setSaving(true);
    try {
      const rows = selectedDistricts.map((district) => ({ state: newState, district }));
      const { data: res } = await api.post("/permissible-territories/bulk-import", { rows });
      toast.success(`Added ${res.created_count} district${res.created_count === 1 ? "" : "s"}${res.skipped_count ? ` · ${res.skipped_count} already existed` : ""}`);
      setOpen(false); reload();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setSaving(false); }
  }

  function startEdit(item) { setEditing(item); setEditPins((item.excluded_pins || []).join(", ")); }

  async function saveEdit(e) {
    e.preventDefault();
    setEditSaving(true);
    try {
      await api.put(`/permissible-territories/${editing.id}`, {
        state: editing.state,
        district: editing.district,
        excluded_pins: editPins.split(",").map((p) => p.trim()).filter(Boolean),
      });
      toast.success("Updated");
      setEditing(null); reload();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setEditSaving(false); }
  }

  async function remove(item) {
    if (!window.confirm(`Remove ${item.district}, ${item.state} from the permissible list?`)) return;
    await api.delete(`/permissible-territories/${item.id}`);
    toast.success("Removed"); reload();
  }

  const grouped = useMemo(() => {
    const byState = {};
    (data || []).forEach((t) => { (byState[t.state] = byState[t.state] || []).push(t); });
    return Object.entries(byState).sort(([a], [b]) => a.localeCompare(b));
  }, [data]);

  return (
    <div data-testid="permissible-territories-page">
      <PageHeader
        overline="Master Data · Service Area Gating"
        title="Permissible Territories"
        action={<PrimaryButton onClick={startCreate} testid="create-territory-btn"><Plus className="w-4 h-4" /> Add Districts</PrimaryButton>}
      />
      <p className="text-xs font-mono text-[#4A5568] mb-4 max-w-2xl">
        Only districts listed here are eligible for new Lead / new-installation Ticket creation for
        customers whose PIN resolves outside this list. New customer onboarding is never blocked — this
        check applies only when raising a new Lead or a new-installation Ticket.
      </p>

      <div className="border border-[#E2E4DC] bg-white">
        {grouped.length === 0 && (
          <div className="px-4 py-8 text-center text-sm text-[#4A5568]">No permissible territories yet — click "Add Districts" to start.</div>
        )}
        {grouped.map(([state, rows]) => (
          <div key={state} className="border-b border-[#E2E4DC] last:border-b-0">
            <div className="px-4 py-2 bg-[#F4F6F1] font-mono text-[10px] uppercase tracking-[0.25em] text-[#1F4E2C] font-bold">{state} · {rows.length} district{rows.length === 1 ? "" : "s"}</div>
            <table className="w-full text-sm">
              <tbody>
                {rows.sort((a, b) => a.district.localeCompare(b.district)).map((item) => (
                  <tr key={item.id} className="border-b border-[#F0F1EB] hover:bg-[#F9FAF7]" data-testid={`territory-row-${item.id}`}>
                    <td className="px-4 py-2.5 w-64 font-mono text-sm flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-[#94A3B8]" />{item.district}</td>
                    <td className="px-4 py-2.5 font-mono text-xs text-[#4A5568]">
                      {(item.excluded_pins || []).length > 0 ? <>Excluded PINs: {item.excluded_pins.join(", ")}</> : <span className="text-[#94A3B8] italic">No excluded PINs</span>}
                    </td>
                    <td className="px-4 py-2.5 text-right w-20">
                      <button onClick={() => startEdit(item)} data-testid={`edit-territory-${item.id}`} className="p-1.5 hover:text-[#1F4E2C]"><Edit3 className="w-4 h-4" /></button>
                      <button onClick={() => remove(item)} data-testid={`delete-territory-${item.id}`} className="p-1.5 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title="Add Permissible Districts">
        <form onSubmit={save} className="space-y-4">
          <Field label="State *">
            <Select required value={newState} onChange={(e) => { setNewState(e.target.value); setSelectedDistricts([]); }} data-testid="territory-state">
              <option value="">— Select State —</option>
              {INDIA_STATES_DISTRICTS.map((s) => <option key={s.state} value={s.state}>{s.state}</option>)}
            </Select>
          </Field>
          {newState && (
            <Field label={`Districts * (${selectedDistricts.length} selected)`}>
              <div className="max-h-64 overflow-y-auto border border-[#E2E4DC] grid grid-cols-2 gap-1 p-2" data-testid="territory-district-multiselect">
                {districtOptions.map((d) => {
                  const listed = alreadyListed.has(d);
                  const checked = selectedDistricts.includes(d);
                  return (
                    <button
                      type="button"
                      key={d}
                      disabled={listed}
                      onClick={() => toggleDistrict(d)}
                      data-testid={`territory-district-option-${d}`}
                      className={
                        "flex items-center gap-1.5 px-2 py-1.5 text-left text-xs font-mono border " +
                        (listed ? "opacity-40 cursor-not-allowed border-transparent" :
                          checked ? "bg-[#1F4E2C] text-white border-[#1F4E2C]" : "border-[#E2E4DC] hover:border-[#1F4E2C]")
                      }
                    >
                      <span className={"w-3.5 h-3.5 border flex items-center justify-center " + (checked ? "border-white" : "border-[#94A3B8]")}>
                        {checked && <Check className="w-2.5 h-2.5" />}
                      </span>
                      {d}{listed ? " (added)" : ""}
                    </button>
                  );
                })}
              </div>
            </Field>
          )}
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={() => setOpen(false)} className="px-4 py-2 font-mono text-xs uppercase tracking-widest text-[#4A5568]">Cancel</button>
            <PrimaryButton type="submit" disabled={saving} testid="territory-save-btn">{saving ? "Saving…" : `Add ${selectedDistricts.length || ""} District${selectedDistricts.length === 1 ? "" : "s"}`}</PrimaryButton>
          </div>
        </form>
      </Modal>

      <Modal open={!!editing} onClose={() => setEditing(null)} title="Edit Excluded PINs">
        {editing && (
          <form onSubmit={saveEdit} className="space-y-4">
            <div className="text-sm font-mono text-[#0F1A12] font-bold">{editing.district}, {editing.state}</div>
            <Field label="Excluded PIN codes (comma-separated, optional)">
              <TextInput value={editPins} onChange={(e) => setEditPins(e.target.value)} placeholder="560001, 560002" data-testid="territory-excluded-pins" />
            </Field>
            <div className="flex justify-end gap-3 pt-2">
              <button type="button" onClick={() => setEditing(null)} className="px-4 py-2 font-mono text-xs uppercase tracking-widest text-[#4A5568]">Cancel</button>
              <PrimaryButton type="submit" disabled={editSaving} testid="territory-edit-save-btn">{editSaving ? "Saving…" : "Save"}</PrimaryButton>
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
}
